/**
 * CuratedSection — reusable component that renders a single curated section
 * (hero / ofertas_semana / remates / premium / recomendados / nuevos)
 * produced by /api/public/groupings/sections. Used by both the home page
 * (Landing) and the storefront.
 */
import { Link } from "react-router-dom";
import { Plus } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { trackClick, useImpressionTracker } from "@/lib/exposure";

const SECTION_TITLES = {
  hero: "Destacados",
  ofertas_semana: "Ofertas de la semana",
  remates: "Remates",
  premium: "Premium",
  recomendados: "Recomendados",
  nuevos: "Nuevos ingresos",
};

const fmtPrice = (n, currency = "MXN") => {
  if (n === null || n === undefined) return "—";
  const value = `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return currency === "USD" ? `${value} USD` : value;
};

export default function CuratedSection({ section, vendorRef, theme = "light" }) {
  const { addItem } = useCart();
  const accent = section.accent_color || "#002FA7";
  const isDark = theme === "dark";
  const sectionLabel = SECTION_TITLES[section.section] || section.name;

  const handleAdd = (it) => {
    if (it.kind === "product") {
      const price = it.promotion ? it.promotion.promo_price : it.store_price;
      const currency = it.promotion?.currency || it.currency || "MXN";
      addItem({ id: it.id, name: it.name, brand: it.brand, photos: [it.photo].filter(Boolean), store_price: price, sale_currency: currency, max_qty: 5 }, 1);
    } else if (it.kind === "promotion") {
      addItem({ id: it.product_id, name: it.name, brand: it.brand, photos: [it.photo].filter(Boolean), store_price: it.promo_price, sale_currency: it.currency || "MXN", max_qty: 5 }, 1);
    } else if (it.kind === "combo") {
      addItem({ id: it.id, name: it.name, brand: "COMBO", photos: [it.photo].filter(Boolean), store_price: it.price, sale_currency: it.currency || "MXN", max_qty: it.max_qty || 5, kind: "combo" }, 1);
    }
  };

  return (
    <section data-testid={`section-${section.section}`}>
      {section.banner_url ? (
        <div className="relative aspect-[5/2] sm:aspect-[6/1.6] rounded-xl overflow-hidden mb-6 shadow-xl ring-1 ring-white/10">
          <img src={section.banner_url} alt={section.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-transparent flex items-end p-5 sm:p-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/70 font-mono mb-2">{sectionLabel}</div>
              <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-white">{section.name}</h2>
              {section.description && <p className="text-white/80 text-sm sm:text-base mt-2 max-w-xl">{section.description}</p>}
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-mono mb-1" style={{ color: accent }}>{sectionLabel}</div>
            <h2 className={`font-heading text-3xl sm:text-4xl font-extrabold tracking-tight ${isDark ? "text-white" : "text-zinc-900"}`}>{section.name}</h2>
            {section.description && <p className={`text-sm mt-1 max-w-2xl ${isDark ? "text-white/70" : "text-zinc-500"}`}>{section.description}</p>}
          </div>
          <div className="h-px flex-1 max-w-32 sm:max-w-64 mb-2" style={{ backgroundColor: accent + "40" }} />
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {section.items.map((it, idx) => (
          <CuratedItem
            key={`${it.kind}-${it.id}-${idx}`}
            item={it}
            sectionKey={section.section}
            vendorRef={vendorRef}
            isDark={isDark}
            onAdd={handleAdd}
          />
        ))}
      </div>
    </section>
  );
}

function CuratedItem({ item: it, sectionKey, vendorRef, isDark, onAdd }) {
  const isPromo = it.kind === "promotion" || it.promotion;
  const isCombo = it.kind === "combo";
  const price = isCombo ? it.price : (isPromo ? (it.promotion?.promo_price || it.promo_price) : it.store_price);
  const original = it.kind === "product" && it.promotion ? it.store_price : (it.kind === "promotion" ? it.store_price : null);
  const currency = it.promotion?.currency || it.currency || "MXN";
  const badge = isCombo ? "COMBO" : (isPromo ? (it.promotion?.badge_label || it.badge_label || "PROMO") : null);
  const badgeColor = isCombo ? "#7E57C2" : "#E0000F";
  const priceColor = isPromo ? "#E0000F" : (isCombo ? "#7E57C2" : (isDark ? "#FFFFFF" : "#002FA7"));
  const href = isCombo ? "#" : `/catalogo/p/${it.product_id || it.id}${vendorRef ? `?v=${encodeURIComponent(vendorRef)}` : ""}`;

  // Phase 4 — impression + click tracking
  const trackingRefId = it.id;
  const impressionRef = useImpressionTracker({ kind: it.kind, ref_id: trackingRefId, section: sectionKey });

  const onClickCard = () => trackClick({ kind: it.kind, ref_id: trackingRefId, section: sectionKey });
  const onAddClick = (e) => {
    e.preventDefault(); e.stopPropagation();
    trackClick({ kind: it.kind, ref_id: trackingRefId, section: sectionKey });
    onAdd(it);
  };

  return (
    <div ref={impressionRef}
      className={`group relative rounded-lg overflow-hidden hover:shadow-lg transition-all flex flex-col ${isDark ? "bg-white/5 border border-white/10 hover:border-white/30" : "bg-white border border-zinc-200 hover:border-zinc-300"}`}
      data-testid={`section-item-${it.id}`}>
      {badge && (
        <span className="absolute top-2 left-2 z-10 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-sm tracking-wider shadow"
          style={{ backgroundColor: badgeColor }}>
          {badge}
        </span>
      )}
      {(isCombo ? it.free_shipping !== false : (isPromo ? (it.promotion?.free_shipping ?? it.free_shipping) : it.free_shipping)) && (
        <span
          className="absolute top-2 right-2 z-10 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-wide shadow inline-flex items-center gap-1"
          data-testid={`section-free-shipping-${it.id}`}
          title="Envío gratis en Tijuana, Rosarito y Tecate"
        >
          🚚 Gratis
        </span>
      )}
      <Link to={href} onClick={(e) => { if (isCombo) e.preventDefault(); onClickCard(); }} className="block">
        <div className={`aspect-square ${isDark ? "bg-white/5" : "bg-zinc-50"}`}>
          {it.photo ? (
            <img src={it.photo} alt={it.name} loading="lazy" className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-400 text-xs">Sin foto</div>
          )}
        </div>
        <div className="p-3 pb-2">
          {it.brand && (
            <div className={`text-[10px] uppercase tracking-widest font-mono ${isDark ? "text-white/50" : "text-zinc-500"}`}>{it.brand}</div>
          )}
          <div className={`font-medium text-sm leading-tight line-clamp-2 min-h-[2.5rem] ${isDark ? "text-white" : "text-zinc-900"}`}>{it.name}</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-heading text-lg font-extrabold" style={{ color: priceColor }}>{fmtPrice(price, currency)}</span>
            {original && (
              <span className={`text-xs line-through ${isDark ? "text-white/40" : "text-zinc-400"}`}>{fmtPrice(original, currency)}</span>
            )}
          </div>
        </div>
      </Link>
      <button type="button"
        onClick={onAddClick}
        className="mx-3 mb-3 mt-auto h-9 rounded-full text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors hover:opacity-90"
        style={{ backgroundColor: isPromo ? "#E0000F" : (isCombo ? "#7E57C2" : (isDark ? "#FFFFFF" : "#18181b")), color: (!isPromo && !isCombo && isDark) ? "#000" : "#fff" }}
        data-testid={`section-add-${it.id}`}>
        <Plus size={12} weight="bold" /> Agregar
      </button>
    </div>
  );
}
